import React, { useState, useEffect } from "react";
import { X, ZoomIn } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const buildSrc = (img) =>
    (img.image || "").startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${img.image}` : img.image;

const Gallery = () => {
    const [images, setImages] = useState([]);
    const [lightbox, setLightbox] = useState(null);

    useEffect(() => {
        axios.get(`${API}/gallery`).then(r => setImages(r.data)).catch(() => {});
    }, []);

    return (
        <div className="pt-16">
            {/* Hero */}
            <section className="py-20 px-4 bg-[#3D2008]" data-testid="gallery-hero">
                <div className="max-w-7xl mx-auto">
                    <span className="font-marker text-[#C9A84C] text-2xl -rotate-1 inline-block mb-4">See the Vision</span>
                    <h1 className="font-anton text-5xl sm:text-7xl lg:text-8xl text-[#F5EDD6] uppercase leading-none mb-4">Gallery</h1>
                    <p className="font-manrope text-[#F5EDD6]/70 text-xl max-w-2xl">
                        A glimpse of Seeley Lake, the skate community, and the vision for our future park.
                    </p>
                </div>
            </section>

            {/* Masonry Grid */}
            <section className="py-20 px-4 bg-[#F5EDD6]" data-testid="gallery-grid-section">
                <div className="max-w-7xl mx-auto">
                    {images.length === 0 ? (
                        <p className="font-manrope text-center text-[#8A7A65] py-12">Photos coming soon.</p>
                    ) : (
                        <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-0">
                            {images.map((img, i) => (
                                <div
                                    key={img.id || img.image}
                                    className={`relative overflow-hidden border-2 border-[#3D2008] group cursor-pointer mb-6 break-inside-avoid shadow-[6px_6px_0px_0px_rgba(61,32,8,0.2)] hover:shadow-[8px_8px_0px_0px_rgba(196,82,42,0.35)] transition-shadow duration-200 ${i % 3 === 0 ? "rotate-[0.5deg]" : i % 3 === 1 ? "-rotate-[0.3deg]" : "rotate-[0.2deg]"}`}
                                    onClick={() => setLightbox(img)}
                                    data-testid={`gallery-item-${i}`}
                                >
                                    <img
                                        src={buildSrc(img)}
                                        alt={img.alt}
                                        className="w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                        style={{ minHeight: img.tall ? "380px" : "240px", maxHeight: img.tall ? "500px" : "320px" }}
                                    />
                                    <div className="absolute inset-0 bg-[#3D2008]/0 group-hover:bg-[#3D2008]/50 transition-colors duration-300 flex items-center justify-center">
                                        <ZoomIn size={32} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                    </div>
                                    {img.caption && (
                                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#3D2008]/80 to-transparent p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                                            <span className="font-manrope text-sm text-[#F5EDD6] font-semibold">{img.caption}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Lightbox */}
            {lightbox && (
                <div
                    className="fixed inset-0 z-50 bg-[#3D2008]/95 flex items-center justify-center p-4"
                    onClick={() => setLightbox(null)}
                    data-testid="gallery-lightbox"
                >
                    <button
                        className="absolute top-6 right-6 text-[#F5EDD6] hover:text-[#C4522A] transition-colors"
                        onClick={() => setLightbox(null)}
                        data-testid="gallery-lightbox-close"
                    >
                        <X size={32} />
                    </button>
                    <div className="max-w-4xl max-h-[85vh] flex flex-col items-center" onClick={e => e.stopPropagation()}>
                        <img
                            src={buildSrc(lightbox)}
                            alt={lightbox.alt}
                            className="max-h-[75vh] object-contain border-4 border-[#C9A84C] shadow-[8px_8px_0px_0px_rgba(196,82,42,0.5)]"
                        />
                        {lightbox.caption && <p className="font-manrope text-[#F5EDD6]/70 text-sm mt-4">{lightbox.caption}</p>}
                    </div>
                </div>
            )}

            {/* Submit CTA */}
            <section className="py-16 px-4 bg-[#C4522A]">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="font-anton text-4xl text-white uppercase mb-4">Have Photos to Share?</h2>
                    <p className="font-manrope text-white/80 text-lg mb-6">
                        Send us your skate photos, Seeley Lake shots, or community event pictures!
                    </p>
                    <a href="mailto:info@seeleyskatepark.org" data-testid="gallery-submit-btn"
                        className="inline-block px-8 py-4 bg-white text-[#C4522A] font-anton uppercase tracking-wider text-lg border-2 border-[#3D2008] shadow-[4px_4px_0px_0px_#3D2008] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform duration-150">
                        Send Us Your Photos
                    </a>
                </div>
            </section>
        </div>
    );
};

export default Gallery;
